import type { CsvParseResult } from './parse_csv';

/**
 * Bulk-import template columns, in order. Shared by the CSV and Excel templates
 * so both stay in lock-step.
 */
export const IMPORT_TEMPLATE_HEADERS = ['email', 'first_name', 'last_name', 'type'] as const;

/** Example data row shown in the downloadable templates. */
const TEMPLATE_EXAMPLE_ROW = ['alice@example.com', 'Alice', 'Doe', 'internal'];

/** True when the file looks like an Excel workbook (by extension). */
export function isExcelFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return name.endsWith('.xlsx') || name.endsWith('.xls');
}

/**
 * Parses the first worksheet of an .xlsx/.xls workbook into the same shape as
 * {@link parseCsv}: lower-cased, trimmed header names (row 1) and one record per
 * data row keyed by header, with fully-empty rows skipped.
 *
 * ExcelJS is imported lazily so it only downloads when a user actually works
 * with a spreadsheet — keeping it out of the main bundle.
 *
 * @param buffer The workbook file contents.
 * @returns {Promise<CsvParseResult>} Headers + row records.
 */
export async function parseSpreadsheet(buffer: ArrayBuffer): Promise<CsvParseResult> {
  const { Workbook } = await import('exceljs');
  const workbook = new Workbook();
  await workbook.xlsx.load(buffer);

  const sheet = workbook.worksheets[0];
  if (!sheet) return { headers: [], rows: [] };

  const rowCells = (rowNumber: number): string[] => {
    const row = sheet.getRow(rowNumber);
    const cells: string[] = [];
    for (let column = 1; column <= sheet.columnCount; column += 1) {
      // `.text` yields the display string for any cell type (rich text,
      // hyperlink, formula result), unlike `.value`.
      cells.push((row.getCell(column).text ?? '').toString().trim());
    }
    return cells;
  };

  const headers = rowCells(1).map((header) => header.toLowerCase());
  const rows: Array<Record<string, string>> = [];
  for (let rowNumber = 2; rowNumber <= sheet.rowCount; rowNumber += 1) {
    const cells = rowCells(rowNumber);
    if (cells.every((cell) => cell === '')) continue;
    const record: Record<string, string> = {};
    headers.forEach((header, index) => {
      record[header] = cells[index] ?? '';
    });
    rows.push(record);
  }
  return { headers, rows };
}

/**
 * Builds the .xlsx bulk-import template (header row + one example row) as a
 * Blob. ExcelJS is imported lazily.
 *
 * @returns {Promise<Blob>} The workbook, ready to download.
 */
export async function createExcelTemplateBlob(): Promise<Blob> {
  const { Workbook } = await import('exceljs');
  const workbook = new Workbook();
  const sheet = workbook.addWorksheet('Users');
  sheet.addRow([...IMPORT_TEMPLATE_HEADERS]);
  sheet.addRow(TEMPLATE_EXAMPLE_ROW);
  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
}
